import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { ProductImage } from "@/components/ProductImage";
import { fmt } from "@/lib/site";
import { goToQuote } from "@/lib/quote";

export const CartSheet = () => {
    const { items, open, setOpen, updateQty, removeItem, subtotal, iva, total, savings, count, unitPrice, MAYOREO_MIN } = useCart();
    const nav = useNavigate();

    const goCheckout = () => {
        setOpen(false);
        nav("/checkout");
    };

    const requestQuote = () => {
        const detalle = items.map((i) => `${i.quantity}× ${i.product.nombre} (${i.product.sku})`).join("\n");
        const tipo = items.some((i) => i.quantity >= MAYOREO_MIN) ? "mayoreo" : "detal";
        setOpen(false);
        goToQuote(nav, { detalle, tipo });
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
                side="right"
                data-testid="cart-sheet"
                className="w-full sm:max-w-md bg-crema border-l border-linea p-0 flex flex-col"
            >
                <SheetHeader className="p-6 border-b border-linea">
                    <SheetTitle className="font-display font-bold text-2xl text-ink flex items-center gap-3">
                        <ShoppingBag size={22} className="text-naranja" /> Tu carrito
                        <span className="text-sm text-mut font-normal">({count})</span>
                    </SheetTitle>
                    <SheetDescription className="sr-only">Productos en tu carrito de compras</SheetDescription>
                </SheetHeader>

                {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 gap-4">
                        <ShoppingBag size={48} className="text-linea" strokeWidth={1} />
                        <p className="text-sm text-mut">Tu carrito está vacío</p>
                        <button
                            onClick={() => { setOpen(false); nav("/tienda"); }}
                            data-testid="cart-empty-shop-btn"
                            className="mt-2 bg-verde text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-verde-800 transition-colors"
                        >
                            Ir a la tienda
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto p-5 space-y-4">
                            {items.map((it) => {
                                const u = unitPrice(it.product, it.quantity);
                                const may = it.quantity >= MAYOREO_MIN;
                                return (
                                    <div key={it.product.id} className="flex gap-3 bg-hueso border border-linea rounded-xl p-3" data-testid={`cart-item-${it.product.id}`}>
                                        <div className="h-20 w-20 bg-white rounded-lg shrink-0 overflow-hidden">
                                            <ProductImage src={it.product.foto_url} alt={it.product.nombre} className="h-full w-full object-contain p-1.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm leading-snug line-clamp-2 text-ink">{it.product.nombre}</p>
                                            <p className="text-xs text-verde font-semibold mt-0.5">
                                                {fmt(u)} c/u {may && <span className="text-madera-dark">· mayoreo</span>}
                                            </p>
                                            <div className="flex items-center justify-between mt-2">
                                                <div className="flex items-center border border-linea rounded-full bg-white">
                                                    <button onClick={() => updateQty(it.product.id, it.quantity - 1)} data-testid={`cart-dec-${it.product.id}`} className="p-1.5 pl-3 text-mut hover:text-naranja"><Minus size={14} /></button>
                                                    <span className="text-sm w-8 text-center font-medium">{it.quantity}</span>
                                                    <button onClick={() => updateQty(it.product.id, it.quantity + 1)} data-testid={`cart-inc-${it.product.id}`} className="p-1.5 pr-3 text-mut hover:text-naranja"><Plus size={14} /></button>
                                                </div>
                                                <button onClick={() => removeItem(it.product.id)} data-testid={`cart-remove-${it.product.id}`} className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-naranja border border-linea rounded-full px-2.5 py-1 hover:border-naranja transition-colors"><Trash2 size={13} /> Quitar</button>
                                            </div>
                                        </div>
                                        <span className="text-sm font-semibold text-ink shrink-0">{fmt(u * it.quantity)}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="border-t border-linea p-6 space-y-3 bg-hueso">
                            {savings > 0 && (
                                <div className="flex justify-between text-xs text-madera-dark font-medium">
                                    <span>Ahorro por mayoreo</span>
                                    <span>− {fmt(savings)}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-end">
                                <span className="text-xs uppercase tracking-widest text-mut">Subtotal</span>
                                <span className="text-right">
                                    <span className="block text-xs text-mut">{fmt(subtotal)} + IVA {fmt(iva)}</span>
                                    <span data-testid="cart-subtotal" className="font-display font-bold text-3xl text-verde">{fmt(total)}</span>
                                </span>
                            </div>
                            <p className="text-[11px] text-mut">Compra {MAYOREO_MIN}+ unidades de un producto para precio de mayoreo automático.</p>
                            <button
                                onClick={goCheckout}
                                data-testid="cart-checkout-btn"
                                className="w-full bg-naranja text-white rounded-full py-4 text-sm font-semibold hover:bg-naranja-dark transition-colors"
                            >
                                Finalizar pedido
                            </button>
                            <button
                                onClick={requestQuote}
                                data-testid="cart-quote-btn"
                                className="w-full border border-verde text-verde rounded-full py-3 text-sm font-semibold hover:bg-verde hover:text-white transition-colors"
                            >
                                Pedir presupuesto de estos productos
                            </button>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};
